/**
 * Client-side rate limiting utility
 * Helps prevent abuse and excessive API calls
 */

class RateLimiter {
  constructor(maxAttempts = 5, windowMs = 60000) {
    this.maxAttempts = maxAttempts;
    this.windowMs = windowMs;
    this.attempts = new Map();
  }

  // Check if action is allowed for a specific key (e.g., user ID, IP, action type)
  isAllowed(key) {
    const now = Date.now();
    const windowStart = now - this.windowMs;

    // Clean old attempts
    if (this.attempts.has(key)) {
      const keyAttempts = this.attempts.get(key);
      const recentAttempts = keyAttempts.filter(time => time > windowStart);
      this.attempts.set(key, recentAttempts);
    }

    const currentAttempts = this.attempts.get(key) || [];
    return currentAttempts.length < this.maxAttempts;
  }

  // Record an attempt
  recordAttempt(key) {
    const now = Date.now();
    if (!this.attempts.has(key)) {
      this.attempts.set(key, []);
    }
    this.attempts.get(key).push(now);
  }

  // Get remaining attempts
  getRemainingAttempts(key) {
    const now = Date.now();
    const windowStart = now - this.windowMs;
    const currentAttempts = (this.attempts.get(key) || [])
      .filter(time => time > windowStart);
    return Math.max(0, this.maxAttempts - currentAttempts.length);
  }

  // Get time until reset
  getTimeUntilReset(key) {
    const attempts = this.attempts.get(key);
    if (!attempts || attempts.length === 0) return 0;
    
    const oldestAttempt = Math.min(...attempts);
    const resetTime = oldestAttempt + this.windowMs;
    return Math.max(0, resetTime - Date.now());
  }
}

// Specific rate limiters for different operations
export const authRateLimiter = new RateLimiter(10, 300000); // 10 attempts per 5 minutes
export const teamCreationLimiter = new RateLimiter(10, 600000); // 10 teams per 10 minutes
export const joinRequestLimiter = new RateLimiter(10, 300000); // 10 join requests per 5 minutes
export const usernameChangeLimiter = new RateLimiter(10, 3600000); // 10 username changes per hour

// Helper function to check and record rate limit
export const checkRateLimit = (limiter, key, actionName) => {
  if (!limiter.isAllowed(key)) {
    const timeUntilReset = Math.ceil(limiter.getTimeUntilReset(key) / 1000 / 60);
    throw new Error(`Too many ${actionName} attempts. Please try again in ${timeUntilReset} minutes.`);
  }
  limiter.recordAttempt(key);
};
